import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { getQuote } from "../services/api";
import type { IQuoteResponse, IQuoteRequest } from "../services/api";
import { safeCapture } from "../utils/posthog";
import { config } from "../config";

// Buffer de seguridad: refresh automático N segundos antes de expiración
// Esto evita que el quote expire mientras el usuario hace click en "Confirmar"
const EXPIRATION_BUFFER_SECONDS = 5;

// Umbral de advertencia: mostrar warning cuando quedan N segundos
const WARNING_THRESHOLD_SECONDS = 10;

const DEFAULT_QUOTE_DEBOUNCE_MS = 1000;

export interface UseQuoteOptions {
  /**
   * Wait after the last dependency change before fetching a quote.
   * Defaults to `config.QUOTE_DEBOUNCE_MS` or 1000ms.
   */
  debounceMs?: number;
}

interface UseQuoteResult {
  quote: IQuoteResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isExpiringSoon: boolean; // true cuando queda menos de WARNING_THRESHOLD_SECONDS
  timeRemaining: number; // segundos restantes hasta expiración
}

const isAbortError = (err: unknown): boolean => {
  if (axios.isCancel(err)) return true;
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: string; name?: string };
  return e.code === "ERR_CANCELED" || e.name === "CanceledError" || e.name === "AbortError";
};

/**
 * Hook to fetch a quote for a money transfer
 * The quote is fetched when all required parameters are provided
 *
 * Debounces input changes, and aborts any in-flight quote request when a newer
 * one is started so only the latest response is applied.
 *
 * Automatically refreshes the quote before it expires (with a buffer)
 * to prevent the quote from expiring while the user is confirming.
 *
 * @param sourceCountry - Source country code (e.g., 'US')
 * @param destinationCountry - Destination country code (e.g., 'MX')
 * @param fundingMethod - Funding method ('card' | 'bank_account' | 'cash')
 * @param payoutMethod - Payout method ('card' | 'bank_account')
 * @param amount - Amount to send (null if not yet entered)
 * @param currency - Currency of the amount (e.g., 'USD')
 * @param cardId - Card ID for card payments (will be signed in the quote)
 * @param options - Optional debounce / fetch tuning
 */
export const useQuote = (
  sourceCountry: string,
  destinationCountry: string,
  fundingMethod: string | null,
  payoutMethod: string,
  amount: number | null,
  currency: string,
  cardId?: string | null,
  options: UseQuoteOptions = {}
): UseQuoteResult => {
  const debounceMs =
    options.debounceMs ??
    config.QUOTE_DEBOUNCE_MS ??
    DEFAULT_QUOTE_DEBOUNCE_MS;

  const [quote, setQuote] = useState<IQuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  // Ref para evitar auto-refresh múltiples
  const isRefreshing = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchQuote = useCallback(async () => {
    // Cancel any in-flight quote so only the latest request wins
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Don't fetch if required params are missing
    if (!fundingMethod || !amount || amount <= 0) {
      setQuote(null);
      setLoading(false);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      isRefreshing.current = false;
      return;
    }

    setLoading(true);
    setError(null);

    safeCapture("ux.send.quote.quote.requested", {
      source_country: sourceCountry,
      destination_country: destinationCountry,
      funding_method: fundingMethod,
      payout_method: payoutMethod,
      amount,
      currency,
    });

    try {
      const request: IQuoteRequest = {
        sourceCountry,
        destinationCountry,
        fundingMethod,
        payoutMethod,
        amount,
        currency,
      };

      // Include cardId if provided (for card payments)
      if (cardId) {
        request.cardId = cardId;
      }

      const result = await getQuote(request, controller.signal);

      // Stale / aborted — ignore
      if (controller.signal.aborted || abortControllerRef.current !== controller) {
        return;
      }

      setQuote(result);

      safeCapture("ux.send.quote.quote.received", {
        success: true,
        quote_id: result.quoteId,
        send_amount: result.sendAmount,
        receive_amount: result.receiveAmount,
        exchange_rate: result.exchangeRate,
        total_fee: result.totalFee,
        source_currency: result.sourceCurrency,
        destination_currency: result.destinationCurrency,
        funding_method: result.fundingMethod,
        payout_method: result.payoutMethod,
      });

      // Calcular tiempo inicial restante
      const expiresAt = new Date(result.expiresAt).getTime();
      const now = Date.now();
      const remainingMs = expiresAt - now;
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      isRefreshing.current = false;
    } catch (err) {
      // Aborted because a newer request started — keep previous quote / loading
      if (
        isAbortError(err) ||
        controller.signal.aborted ||
        abortControllerRef.current !== controller
      ) {
        return;
      }

      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch quote";
      safeCapture("ux.send.quote.quote.received", {
        success: false,
        error: errorMessage,
        error_code: "QUOTE_FETCH_FAILED",
      });
      setError(err instanceof Error ? err : new Error("Failed to fetch quote"));
      setQuote(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      isRefreshing.current = false;
    } finally {
      // Only the active request clears loading
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  }, [
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod,
    amount,
    currency,
    cardId,
  ]);

  // Debounce fetch after amount / payment params change.
  // Mark loading immediately so the confirm CTA stays disabled while waiting
  // (avoids confirming with a stale quote, e.g. amount 50 with quote for 5).
  useEffect(() => {
    if (!fundingMethod || !amount || amount <= 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }

    const timeoutId = setTimeout(() => {
      fetchQuote();
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [fetchQuote, debounceMs, fundingMethod, amount]);

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Timer para actualizar timeRemaining y auto-refresh cuando expira
  useEffect(() => {
    if (!quote?.expiresAt) {
      return;
    }

    const intervalId = setInterval(() => {
      const expiresAt = new Date(quote.expiresAt).getTime();
      const now = Date.now();
      const remainingMs = expiresAt - now;
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      // Auto-refresh si quedan menos de EXPIRATION_BUFFER_SECONDS segundos
      // y no estamos ya refrescando (bypasses debounce — call fetchQuote directly)
      if (remainingSeconds <= EXPIRATION_BUFFER_SECONDS && !isRefreshing.current) {
        console.log(
          `[useQuote] Auto-refreshing quote (${remainingSeconds}s remaining)`
        );
        isRefreshing.current = true;
        fetchQuote();
      }

      // Si el quote expiró completamente (por si el auto-refresh falló)
      if (remainingSeconds <= 0 && !isRefreshing.current) {
        console.log("[useQuote] Quote expired, forcing refresh");
        isRefreshing.current = true;
        fetchQuote();
      }
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(intervalId);
  }, [quote?.expiresAt, fetchQuote]);

  return {
    quote,
    loading,
    error,
    refetch: fetchQuote,
    isExpiringSoon,
    timeRemaining,
  };
};
